/**
 * Demo Data Context
 *
 * Provides a single source of truth for demo mode data.
 * All components can pull consistent demo data from this context,
 * ensuring metrics, campaign classifications, and funnel stages match
 * across the entire application.
 *
 * Usage:
 *   1. Wrap your app with <DemoDataProvider>
 *   2. Use useDemoData() hook to access demo data
 *   3. Data is automatically generated based on the current workspace's scenario
 */

import React, { createContext, useContext, useCallback, useMemo, useState, useEffect } from 'react';
import { info, warn } from '../services/logger';
import type {
  CampaignMetricsData,
  ChannelPerformance,
  BrokerPerformance,
} from '@/services/mediaIntelligenceService';
import type { FunnelData } from '@/services/demoDataService';
import {
  DEMO_CONFIG,
  getDemoCampaignMetrics,
  getDemoChannelPerformance,
  getDemoFunnelData,
  getDemoBrokerPerformance,
  loadStaticDemoData,
  hasStaticDemoData,
  USE_STATIC_DEMO_DATA,
  type KPIExpectations,
  type StaticDemoDataJson,
} from '@/services/demoDataService';

// ============================================================================
// TYPES
// ============================================================================

export interface DemoDataContextValue {
  /** Whether demo mode is active */
  isDemo: boolean;
  /** Demo workspace ID */
  workspaceId: string | null;
  /** Campaign metrics data */
  campaigns: CampaignMetricsData;
  /** Channel performance data */
  channelPerformance: ChannelPerformance[];
  /** Funnel breakdown data */
  funnelData: FunnelData;
  /** Broker performance data */
  brokers: BrokerPerformance[];
  /** KPI expectations */
  kpiExpectations: KPIExpectations | null;
  /** Refresh demo data (regenerate with new random values) */
  refreshData: () => void;
  /** Whether static demo data is being used */
  usingStaticData: boolean;
  /** Whether demo data is currently loading */
  isLoading: boolean;
}

interface DemoDataProviderProps {
  children: React.ReactNode;
  /** Force demo mode on/off (optional, defaults to VITE_DEMO_MODE env var) */
  isDemo?: boolean;
}

// ============================================================================
// CONTEXT
// ============================================================================

const DemoDataContext = createContext<DemoDataContextValue | null>(null);

// ============================================================================
// PROVIDER
// ============================================================================

/**
 * Demo Data Provider Component
 *
 * Wraps the application to provide consistent demo data to all components.
 */
export const DemoDataProvider: React.FC<DemoDataProviderProps> = ({
  children,
  isDemo: propIsDemo,
}) => {
  // Check if demo mode is enabled (from env var or prop)
  const isDemo = propIsDemo ?? import.meta.env.VITE_DEMO_MODE === 'true';

  const [refreshKey, setRefreshKey] = useState(0);
  const [staticData, setStaticData] = useState<StaticDemoDataJson | null>(null);
  const [useStatic, setUseStatic] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load static demo data on mount if enabled
  useEffect(() => {
    const loadStatic = async () => {
      if (!isDemo) {
        setUseStatic(false);
        setIsLoading(false);
        return;
      }

      if (!USE_STATIC_DEMO_DATA) {
        setUseStatic(false);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      const hasData = await hasStaticDemoData();

      if (hasData) {
        const data = await loadStaticDemoData();
        if (data) {
          setStaticData(data);
          setUseStatic(true);
          info('DemoDataContext', 'Using static demo data');
        } else {
          setUseStatic(false);
          warn('DemoDataContext', 'Static data load failed, using generator');
        }
      } else {
        setUseStatic(false);
        info('DemoDataContext', 'No static data found, using generator');
      }

      setIsLoading(false);
    };

    loadStatic();
  }, [isDemo]);

  // Refresh demo data
  const refreshData = useCallback(() => {
    setRefreshKey(prev => prev + 1);
    setStaticData(null);
    setUseStatic(false);
  }, []);

  // Generate demo data
  const demoData = useMemo(() => {
    if (!isDemo) {
      return {
        campaigns: {} as CampaignMetricsData,
        channelPerformance: [],
        funnelData: {} as FunnelData,
        brokers: [],
        kpiExpectations: null,
        workspaceId: null,
      };
    }

    // Use static data if available and enabled
    if (useStatic && staticData) {
      return {
        campaigns: staticData.campaigns,
        channelPerformance: staticData.channelPerformance,
        funnelData: staticData.funnelData,
        brokers: staticData.brokers,
        kpiExpectations: staticData.kpiExpectations ?? null,
        workspaceId: staticData.workspaceId ?? null,
      };
    }

    // Fall back to generator
    return {
      campaigns: getDemoCampaignMetrics(),
      channelPerformance: getDemoChannelPerformance(),
      funnelData: getDemoFunnelData(),
      brokers: getDemoBrokerPerformance(),
      kpiExpectations: DEMO_CONFIG.kpiExpectations ?? null,
      workspaceId: DEMO_CONFIG.workspaceId ?? null,
    };
    // refreshKey is intentionally included to trigger re-computation on manual refresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemo, refreshKey, useStatic, staticData]);

  const contextValue: DemoDataContextValue = useMemo(() => ({
    isDemo,
    workspaceId: demoData.workspaceId,
    campaigns: demoData.campaigns,
    channelPerformance: demoData.channelPerformance,
    funnelData: demoData.funnelData,
    brokers: demoData.brokers,
    kpiExpectations: demoData.kpiExpectations,
    refreshData,
    usingStaticData: useStatic,
    isLoading,
  }), [isDemo, demoData, refreshData, useStatic, isLoading]);

  return (
    <DemoDataContext.Provider value={contextValue}>
      {children}
    </DemoDataContext.Provider>
  );
};

// ============================================================================
// HOOK
// ============================================================================

/**
 * useDemoData Hook
 *
 * Access demo data from any component within the DemoDataProvider.
 * Throws an error if used outside of a DemoDataProvider.
 *
 * @returns DemoDataContextValue
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { campaigns, isDemo, refreshData } = useDemoData();
 *
 *   if (!isDemo) {
 *     return <div>Loading real data...</div>;
 *   }
 *
 *   return (
 *     <div>
 *       <h2>Demo Campaigns ({campaigns.length})</h2>
 *       <button onClick={refreshData}>Refresh Data</button>
 *       {campaigns.map(campaign => (
 *         <div key={campaign.campaign_id}>{campaign.campaign_name}</div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export const useDemoData = (): DemoDataContextValue => {
  const context = useContext(DemoDataContext);

  if (!context) {
    throw new Error('useDemoData must be used within a DemoDataProvider');
  }

  return context;
};

// ============================================================================
// UTILITY HOOKS
// ============================================================================

/**
 * useIsDemo Hook
 *
 * Quick check if demo mode is active.
 * Useful for conditional rendering.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const isDemo = useIsDemo();
 *   return isDemo ? <DemoView /> : <LiveView />;
 * }
 * ```
 */
export const useIsDemo = (): boolean => {
  const context = useContext(DemoDataContext);
  return context?.isDemo ?? import.meta.env.VITE_DEMO_MODE === 'true';
};

// ============================================================================
// EXPORTS
// ============================================================================

export default DemoDataContext;
